import { DeviceQuantityMap, OptionType } from './index';

export type OBFinalizationStatus = {
  timestamp: string;
} | null;

export type OBContact = {
  firstName: string;
  lastName: string;
  email: string;
  primaryNumber: string;
  primaryNumberType: 'office' | 'mobile';
  primaryNumberId: string;
  primaryExtensionNumber: string;
  secondaryNumber: string;
  secondaryNumberType: 'office' | 'mobile';
  secondaryNumberId: string;
  secondaryExtensionNumber: string;
  role: string;
} & OBContactExtraAttributes;

export type InvalidTarget = {
  target: string;
  reason: string;
  type: string;
};

export type OBInternetExposure = Readonly<{
  ipAddresses: string;
  dnsDomains: string;
  noExternalHosts: boolean;
  vulScanIPsConfigured: boolean;
  completed: boolean;
  domainOwnershipConfirmed: boolean;
  finalized: OBFinalizationStatus;
  invalidTargets: InvalidTarget[];
}>;

export type OBSites = {
  readonly sites: OBIdToSiteMap;
  readonly completed: boolean;
  readonly finalized: OBFinalizationStatus;
  readonly configureConnectWise: boolean;
};

export type OBIdToSiteMap = Record<string, OBSite>;

export enum OBLogSourceIAM {
  AD = 'Active Directory',
  ADALLOM = 'Adallom',
  OKTA = 'Okta',
  RADIUS = 'RADIUS',
  SAILPOINT = 'Sailpoint',
  SAMBA = 'SAMBA',
  OTHER = 'Other (put in notes)',
}

export enum OBLogSourceDNS {
  AD = 'Active Directory DNS',
  INFOBLOX = 'Infoblox',
  UMBRELLA_OPENDNS = 'Umbrella OpenDNS',
  OTHER = 'Other (put in notes)',
}

export enum OBLogSourceDHCP {
  WINDOWS_DOMAIN_CONTROLLER = 'Windows Domain Controller',
  WINDOWS_OTHER = 'Other Windows Server',
  OTHER = 'Firewall/Other (put in notes)',
}

export enum OBLogSourceAntiVirus {
  BIT9 = 'Bit9',
  CARBONBLACK = 'CarbonBlack',
  CARBONBLACK_DEFENSE = 'CarbonBlack Defense',
  CROWDSTRIKE = 'Crowdstrike',
  CYLANCEPROTECT = 'CylanceProtect',
  CYLANCEOPTICS = 'CylanceOptics',
  KASPERSKY = 'Kaspersky',
  MCAFEE_EPO = 'McAfee EPO',
  OSSEC = 'OSSEC',
  SENTINELONE = 'SentinelOne',
  SOPHOS = 'Sophos',
  SYMANTEC = 'Symantec',
  WEBROOT = 'Webroot',
  CISCOAMP = 'Cisco AMP',
  OTHER = 'Other (put in notes)',
}

// TODO get a confirmed list from bernie
export enum OBLogSourceMFA {
  NONE = 'None',
}

export enum OBLogSourceIDS {
  SENTINEL = 'Sentinel',
  FIREPOWER = 'Firepower',
  FIREEYE = 'Fireeye',
  OTHER = 'Other (put in notes)',
}

// TODO get a confirmed list from bernie
export enum OBLogSourceVPN {
  NONE = 'None',
}

export enum OBLogSourceProxy {
  BARRACUDA = 'Barracuda',
  CISCO_IRONPORT = 'Cisco-Ironport',
  CITRIX_NETSCALER = 'Citrix Netscaler',
  IPRISM = 'IPRISM',
  PROXYSG = 'ProxySG',
  WEBSENSE = 'WebSense',
  ZSCALER = 'ZScaler',
  OTHER = 'Other (put in notes)',
}

export type OBLogSource = {
  iam: string[];
  dns: string[];
  dhcp: string[];
  antivirus: string[];
  mfa: string | '';
  ids: string[];
  wap: string[];
  vpn: string | '';
  proxy: OBLogSourceProxy | '';
  cls: string | ''; // custom log sources
};

export type OBSite = {
  name: string;
  address: OBAddress;
  timezone: string;
  firewalls: string[];
  switches: string[];
  ipSubnets: string;
  networkBlacklist: string;
  scanningBlacklist: string;
  devices: string;
  adc: number; // active directory controllers
  isADCDHCP: boolean;
  isADCDNS: boolean;
  thirdPartySite: boolean;
  oldestWindowsVersion: string;
  notes: string;
  finalized: OBFinalizationStatus;
} & OBLogSource;

// Derived from https://github.com/rtkwlf/salesforce/blob/master/internal/openapi/model_cloud_sensor_type.go
export enum OBCloudSensorTypes {
  aws = 'aws',
  o365 = 'o365',
  salesforce = 'salesforce',
  azure = 'azure',
  box = 'box',
  gsuite = 'gsuite',
  gcp = 'gcp', // make sure it is the same value in SF
  imanage = 'imanage',
}

export enum OBVirtualSensorTypes {
  vlc = 'vlc',
  vsensor = 'vsensor',
}

export enum OBServiceTypes {
  managedDetection = 'managedDetection',
  managedRisk = 'managedRisk',
  cspmAzure = 'cspmAzure',
  cspmAws = 'cspmAws',
  cspmGcp = 'cspmGcp',
  logSearch = 'logSearch',
  managedAwareness = 'awareness',
  managedAwarenessPlus = 'awarenessPlus',
  contentPack = 'contentPack',
  dataExploration = 'dataExploration',
  irJumpstartRetainer = 'irJumpstartRetainer', //legacy
  jumpStartRetainer = 'jumpStartRetainer',
  incident360 = 'incident360',
  incident360Plus = 'incident360Plus',
  rapidResponse = 'rapidResponse',
  legacyConciergeTier1 = 'legacyConciergeTier1',
  legacyConciergeTier2 = 'legacyConciergeTier2',
  legacyConciergeTier3 = 'legacyConciergeTier3',
}

export type OBShippingContactInfo = {
  isShippingToNewContact: boolean;
  shippingContactId: string;
  shippingContactFirstName: string;
  shippingContactLastName: string;
  shippingContactEmail: string;
  shippingContactPhoneNumber: string;
  shippingContactPhoneType: 'office' | 'mobile';
};

export type OBShippingAddressInfo = {
  isShippingToNewSite: boolean;
  shippingSiteId: string;
  shippingSiteLocation: OBAddress;
};

export type OBHardwareShippingInfo = OBShippingContactInfo &
  OBShippingAddressInfo & {
    shippingNotes: string;
  };

export type OBHardwareIPConfig = {
  isIPAutoConfig: boolean; // if true, the other fields are irrelevant

  ipAddress: string;
  subnetMask: string;
  gateway: string;
  primaryDns: string;
  secondaryDns: string;
};

export enum OBSensorConfigurations {
  SPAN_MIRROR = 'SPAN/Mirror',
  INTERNAL_TAP = 'Internal Tap',
}

export enum OBSensorDeploymentCapabilities {
  MIRROR = 'mirror',
  INTERNAL_TAP = 'internal-tap',
  MIRRORING = 'mirroring',
  INTERNALTAP = 'internal_tap',
}

export type OBSensor = OBSensorInfo & OBSensorNetwork & OBHardwareShippingInfo;

export type OBSensorInfo = {
  name: string;
  model: string;
  siteId: string;
  series: string;
  recommendationOverridden: boolean; //
  /***
   * For tracking if the sensor model has been manually changed.
   * This can be true but recommendationOverridden can still be false if the sensor hasn't been saved
   */
  modelChanged?: boolean;
  configurationTaskID: number;
};

export type OBScannerInfo = Omit<
  OBSensorInfo,
  'series' | 'recommendationOverridden' | 'modelChanged'
> & {
  vanilla: boolean;
  configurationTaskID: number;
};

export type OBSensorNetwork = {
  packetConfig: OBSensorConfigurations;
  currentCapabilities: Record<string, string>;
} & OBHardwareIPConfig;

export type SensorCapabilities = Record<string, SensorCapability[]>;
export type SensorCapabilitiesResponse = { capabilities: SensorCapability[] };
export type SensorRecommendations = { models: string[]; compatible: string[] };

export type SensorCapability = {
  capabilityType: string;
  tooltip: string;
  label: string;
  required?: boolean;
  options: OptionType[];
};

export type OBSensorShipping = OBHardwareShippingInfo;

export type OBScanner = Omit<
  OBSensor,
  | 'packetConfig'
  | 'model'
  | 'currentCapabilities'
  | 'series'
  | 'recommendationOverridden'
> & {
  model: string;
  vanilla: boolean;
};

export type OBSensors = {
  readonly sensors: OBIdToSensorMap;
  readonly completed: boolean;
  readonly loading: boolean;
};

export type OBIdToSensorMap = Record<string, OBSensor>;

export type OBScanners = {
  readonly scanners: OBIdToScannerMap;
  readonly completed: boolean;
  readonly loading: boolean;
};

export type OBIdToScannerMap = Record<string, OBScanner>;

export type OBContactExtraAttributes = {
  isDL: boolean; // false means it is a user
  contactType: OBContactType;
  isBasicEscalationContact: boolean;
  isReportRecipient: boolean;
  isPortalUser: boolean;
  isLogSearchUser: boolean;
  isRiskUser: boolean;
  isMSP: boolean;
  isShippingContact: boolean;
  isManagedAwarenessUser: boolean;
  isActivateVlcUser: boolean;
  isExecutiveSponsor: boolean;
  isMyCyberDashboardUser: boolean;
};

export type OBContactType = 'primary' | 'secondary' | 'normal';

export type OBContacts = {
  readonly contacts: OBIdToContactMap;
  readonly completed: boolean;
  readonly finalized: OBFinalizationStatus;
  readonly loading: boolean;
};

export type OBIdToContactMap = Record<string, OBContact>;

export type OBAWS = {
  regions: OBAWSRegions[] | null;
  cloudWatch: boolean;
  cloudTrail: boolean;
  guardDuty: boolean;
  acl: boolean;
  eks: boolean;
  completed: boolean;
};

export type OBAzure = {
  logAnalytics: boolean;
  securityCenter: boolean;
  sentinel: boolean;
  completed: boolean;
};

export type OBO365 = {
  licenses: OBO365Licenses[] | null;
  dlp: boolean;
  completed: boolean;
};

export type OBSF = {
  licenses: OBSFLicenses[] | null;
  completed: boolean;
};

export type OBIManage = {
  licenses: OBIManageLicenses[] | null;
  completed: boolean;
};

export type OBGeoLogin = {
  countries: OBGeoLoginCountry[] | null;
  completed: boolean;
};

export type OBGCP = {
  licenses: OBGCPLicenses[];
};

export type OBAWSRegions =
  | 'us-east-1'
  | 'us-east-2'
  | 'us-west-1'
  | 'us-west-2'
  | 'ca-central-1'
  | 'sa-east-1'
  | 'eu-west-1'
  | 'eu-west-2'
  | 'eu-west-3'
  | 'eu-central-1'
  | 'eu-north-1'
  | 'ap-south-1'
  | 'ap-northeast-2'
  | 'ap-southeast-1'
  | 'ap-southeast-2'
  | 'ap-northeast-1'
  | 'af-south-1';

export type OBGeoLoginCountry = string; // Can we refine this better?

export enum OBSFLicenses {
  ESSENTIALS = 'Essentials',
  PROFESSIONAL = 'Professional',
  ENTERPRISE = 'Enterprise',
}

export enum OBIManageLicenses {
  THREAT_MANAGER = 'iManage Threat Manager',
}

export enum OBO365Licenses {
  E3 = 'Office 365 E3',
  E5 = 'Office 365 E5',
  OTHER = 'Office 365 (Other)',
}

export enum OBGCPLicenses {
  STANDARD = 'Standard Tier',
  PREMIUM = 'Premium Tier',
}

export type OBAvailableHardware = {
  sensors: OBHardwareInfo[];
  scanners: OBHardwareInfo[];
};

export type OBHardwareInfo = {
  label: string;
  hardware_key: string;
  supported_configurations?: OBSensorConfigurations[];
  allotment_key: string;
  hidden?: boolean;
};

export type OBVirtualScanner = {
  software: OBVirtualizationSoftware;
  requirementsMet: boolean;
  noVirtualScanners: boolean;
  completed: boolean;
};

export type OBEndpointInfo = {
  name: string;
  value: string;
};

export type OBAvailableEndpoints = {
  onPremiseEndpoints: OBEndpointInfo[];
  cloudEndpoints: OBEndpointInfo[];
};

export type RawOBAvailableEndpoints = {
  on_premise_endpoints: OBEndpointInfo[];
  cloud_endpoints: OBEndpointInfo[];
};

export type OBGenericSensor = {
  label: string;
  value: string;
};

export type OBVirtualSensorInfo = {
  name: string;
  value: string;
};

export type OBAvailableVirtualSensors = {
  sensors: OBVirtualSensorInfo[];
};

export type RawOBAvailableVirtualSensors = {
  sensors: OBVirtualSensorInfo[];
};

export type OBVirtualSensorConfig = {
  hypervisor: string[] | null;
  virtualizationRequirements: boolean;
  noVirtualSensor: boolean;
  completed: boolean;
};

export type OBEndpointConfig = {
  onPremEpps: string[] | null;
  cloudEpps: string[] | null;
  notes: string;
  noOnPremEpps: boolean;
  noCloudEpps: boolean;
  windowsEndpoints: string;
  macEndpoints: string;
  linuxEndpoints: string;
  sysmonInstalled: boolean;
  noAgents: boolean;
  agentCompleted: boolean;
  eppCompleted: boolean;
  completed: boolean;
  finalized: OBFinalizationStatus;
};

export enum OBVirtualizationSoftware {
  VMWARE = 'VMWare',
  HYPERV = 'HyperV',
  AWS = 'AWS',
  AZURE = 'Azure',
  OTHER = 'Other',
  NONE = 'None',
}

export type OBCloudSensors = {
  readonly aws: OBAWS;
  readonly azure: OBAzure;
  readonly o365: OBO365;
  readonly sf: OBSF;
  readonly geo: OBGeoLogin;
  readonly gcp: OBGCP;
  readonly imanage: OBIManage;
  readonly finalized: OBFinalizationStatus;
};

export type OBCloudSensorApplication = keyof Omit<
  OBCloudSensors,
  'finalized' | 'gcp'
>;

export type OBAddress = {
  id: string;
  country: string;
  street: string;
  street2: string;
  city: string;
  region: string;
  postcode: string;
};

export type OBSyncResults = {
  success: string[];
  errors: string[];
};

export type OBTeamContact = {
  first_name: string;
  last_name: string;
  email: string;
};

export type OBTeamContacts = {
  pm?: OBTeamContact;
  pse?: OBTeamContact;
};

export type OBProducts = {
  services: OBServiceTypes[];
  hardwareQuantity: DeviceQuantityMap;
  cloudSensors: OBCloudSensorTypes[];
  virtualSensors: OBVirtualSensorTypes[];
};

export enum OBHardwareType {
  SENSOR = 'sensors',
  SCANNER = 'scanners',
}

export type AccordionFooterPropType = {
  canEdit: boolean;
  isComplete: boolean;
  disabled: boolean;
};

export type URLNetworkConfigMap = {
  dhcp: boolean;
  ip: string;
  netmask: string;
  gateway: string;
  dns_nameservers: string[];
};

export type URLConfigMap = {
  mgmt0: URLNetworkConfigMap;
  deployment_type: string;
  model: string;
  site: string;
  shorthand: string;
};
